import { isZero, nanArray } from './series'

/**
 * The TA-Lib moving-average family (TA_MA matype 0–5), with TA-Lib's lookback
 * and seeding semantics.
 *
 * `startIdx` is where output begins (TA-Lib's startIdx, clamped up to the
 * lookback). It is load-bearing for the EMA family: TA-Lib seeds an EMA with the
 * SMA of the `n` values ENDING at startIdx, so a composite that starts an inner
 * EMA later (MACD, APO, STOCH, BBANDS with an EMA matype, TRIX/DEMA/TEMA chains)
 * gets a different seed than a full-array EMA would. Shift-invariant averages
 * (SMA, WMA, TRIMA) only trim their output.
 */

export const MATYPE = { SMA: 0, EMA: 1, WMA: 2, DEMA: 3, TEMA: 4, TRIMA: 5 } as const

export const MATYPE_OPTIONS = [
  { value: 0, label: 'SMA' },
  { value: 1, label: 'EMA' },
  { value: 2, label: 'WMA' },
  { value: 3, label: 'DEMA' },
  { value: 4, label: 'TEMA' },
  { value: 5, label: 'TRIMA' },
] as const

/** TA_MA_Lookback for the supported matypes. */
export function maLookback(n: number, matype: number): number {
  if (n <= 1) return 0
  switch (matype) {
    case MATYPE.DEMA:
      return 2 * (n - 1)
    case MATYPE.TEMA:
      return 3 * (n - 1)
    default:
      return n - 1
  }
}

/** TA_INT_SMA — rolling sum, output from startIdx (≥ n−1). */
export function sma(src: Float64Array, n: number, startIdx: number = n - 1): Float64Array {
  const len = src.length
  const out = nanArray(len)
  if (n < 1) return out
  const start = Math.max(startIdx, n - 1)
  if (start >= len) return out
  let total = 0
  for (let i = start - n + 1; i < start; i++) total += src[i]
  let trailing = start - n + 1
  for (let i = start; i < len; i++) {
    total += src[i]
    out[i] = total / n
    total -= src[trailing++]
  }
  return out
}

/** TA_INT_EMA (TA_COMPATIBILITY_DEFAULT): seed = SMA of the n values ending at startIdx. */
export function ema(src: Float64Array, n: number, startIdx: number = n - 1): Float64Array {
  const len = src.length
  const out = nanArray(len)
  if (n < 1) return out
  const start = Math.max(startIdx, n - 1)
  if (start >= len) return out
  const k = 2 / (n + 1)
  let seed = 0
  for (let i = start - n + 1; i <= start; i++) seed += src[i]
  let prev = seed / n
  out[start] = prev
  for (let i = start + 1; i < len; i++) {
    prev = (src[i] - prev) * k + prev
    out[i] = prev
  }
  return out
}

/** TA_WMA — weights 1..n. */
export function wma(src: Float64Array, n: number, startIdx: number = n - 1): Float64Array {
  const len = src.length
  const out = nanArray(len)
  if (n < 1) return out
  const start = Math.max(startIdx, n - 1)
  if (start >= len) return out
  const divider = (n * (n + 1)) / 2
  for (let i = start; i < len; i++) {
    let s = 0
    for (let j = 0; j < n; j++) s += src[i - n + 1 + j] * (j + 1)
    out[i] = s / divider
  }
  return out
}

/** TA_DEMA — 2·EMA − EMA(EMA); the inner EMA starts one lookback earlier. */
export function dema(src: Float64Array, n: number, startIdx: number = 2 * (n - 1)): Float64Array {
  const l = n - 1
  const start = Math.max(startIdx, 2 * l)
  const e1 = ema(src, n, start - l)
  const e2 = ema(e1, n, start)
  const out = nanArray(src.length)
  for (let i = start; i < src.length; i++) out[i] = 2 * e1[i] - e2[i]
  return out
}

/** TA_TEMA — 3·EMA1 − 3·EMA2 + EMA3; each inner EMA starts one lookback earlier. */
export function tema(src: Float64Array, n: number, startIdx: number = 3 * (n - 1)): Float64Array {
  const l = n - 1
  const start = Math.max(startIdx, 3 * l)
  const e1 = ema(src, n, start - 2 * l)
  const e2 = ema(e1, n, start - l)
  const e3 = ema(e2, n, start)
  const out = nanArray(src.length)
  for (let i = start; i < src.length; i++) out[i] = 3 * e1[i] - 3 * e2[i] + e3[i]
  return out
}

/** TA_TRIMA — triangular weights (1,2,…,m,…,2,1); even n: 1..n/2,n/2..1. */
export function trima(src: Float64Array, n: number, startIdx: number = n - 1): Float64Array {
  const len = src.length
  const out = nanArray(len)
  if (n < 1) return out
  const start = Math.max(startIdx, n - 1)
  if (start >= len) return out
  const w = new Float64Array(n)
  let sum = 0
  for (let j = 0; j < n; j++) {
    const wj = Math.min(j + 1, n - j)
    w[j] = wj
    sum += wj
  }
  for (let i = start; i < len; i++) {
    let s = 0
    for (let j = 0; j < n; j++) s += src[i - n + 1 + j] * w[j]
    out[i] = s / sum
  }
  return out
}

/** TA_MA dispatch. A period of 1 returns the input (TA-Lib does the same for every matype). */
export function ma(src: Float64Array, n: number, matype: number, startIdx: number = maLookback(n, matype)): Float64Array {
  if (n === 1) {
    const out = nanArray(src.length)
    for (let i = Math.max(0, startIdx); i < src.length; i++) out[i] = src[i]
    return out
  }
  switch (matype) {
    case MATYPE.EMA:
      return ema(src, n, startIdx)
    case MATYPE.WMA:
      return wma(src, n, startIdx)
    case MATYPE.DEMA:
      return dema(src, n, startIdx)
    case MATYPE.TEMA:
      return tema(src, n, startIdx)
    case MATYPE.TRIMA:
      return trima(src, n, startIdx)
    default:
      return sma(src, n, startIdx)
  }
}

/**
 * TA_KAMA — Kaufman's adaptive MA; lookback n. Per bar the efficiency ratio is
 * |close − close[n bars ago]| / Σ|1-bar changes| over those n bars (forced to 1
 * when the sum is ≤ the n-bar change or TA_IS_ZERO), the smoothing constant is
 * (ER·(2/3 − 2/31) + 2/31)², and KAMA updates like an EMA with it. The first
 * KAMA (at index startIdx) is seeded from the close before it, and the sum of
 * changes slides by removing |trailing − new trailing| and adding the newest
 * change — transcribed from ta_KAMA.c (v0.6.4).
 */
export function kama(src: Float64Array, n: number, startIdx: number = n): Float64Array {
  const len = src.length
  const out = nanArray(len)
  if (n < 2) return out
  const start = Math.max(startIdx, n)
  if (start >= len) return out
  const constMax = 2 / (30 + 1)
  const constDiff = 2 / (2 + 1) - constMax
  let sumROC1 = 0
  let today = start - n
  let trailingIdx = today
  for (let i = n; i > 0; i--) {
    let tempReal = src[today++]
    tempReal -= src[today]
    sumROC1 += Math.abs(tempReal)
  }
  // The close before the first output is used as the previous KAMA.
  let prevKAMA = src[today - 1]
  let tempReal = src[today]
  let tempReal2 = src[trailingIdx++]
  let periodROC = tempReal - tempReal2
  let trailingValue = tempReal2
  tempReal = sumROC1 <= periodROC || isZero(sumROC1) ? 1 : Math.abs(periodROC / sumROC1)
  tempReal = tempReal * constDiff + constMax
  tempReal *= tempReal
  prevKAMA = (src[today++] - prevKAMA) * tempReal + prevKAMA
  // (TA-Lib's "skip the unstable period" loop never runs: the unstable period is 0.)
  out[today - 1] = prevKAMA
  while (today < len) {
    tempReal = src[today]
    tempReal2 = src[trailingIdx++]
    periodROC = tempReal - tempReal2
    sumROC1 -= Math.abs(trailingValue - tempReal2)
    sumROC1 += Math.abs(tempReal - src[today - 1])
    trailingValue = tempReal2
    tempReal = sumROC1 <= periodROC || isZero(sumROC1) ? 1 : Math.abs(periodROC / sumROC1)
    tempReal = tempReal * constDiff + constMax
    tempReal *= tempReal
    prevKAMA = (src[today++] - prevKAMA) * tempReal + prevKAMA
    out[today - 1] = prevKAMA
  }
  return out
}

/**
 * TA_T3 — Tillson's T3: six chained EMAs (k = 2/(n+1)) combined as
 * c1·e6 + c2·e5 + c3·e4 + c4·e3 with c1 = −b³, c2 = 3b² + 3b³,
 * c3 = −6b² − 3b − 3b³, c4 = 1 + 3b + b³ + 3b² (b = vfactor); lookback 6(n−1).
 * Each stage is seeded with the SMA of the first n values of the previous stage
 * (its seed included), the seeding loops running one after another exactly as in
 * ta_T3.c (v0.6.4); the whole chain advances one bar per input bar.
 */
export function t3(src: Float64Array, n: number, vfactor: number, startIdx: number = 6 * (n - 1)): Float64Array {
  const len = src.length
  const out = nanArray(len)
  if (n < 1) return out
  const lookback = 6 * (n - 1)
  const start = Math.max(startIdx, lookback)
  if (start >= len) return out
  let today = start - lookback
  const k = 2 / (n + 1)
  const oneMinusK = 1 - k
  let tempReal = src[today++]
  for (let i = n - 1; i > 0; i--) tempReal += src[today++]
  let e1 = tempReal / n
  tempReal = e1
  for (let i = n - 1; i > 0; i--) {
    e1 = k * src[today++] + oneMinusK * e1
    tempReal += e1
  }
  let e2 = tempReal / n
  tempReal = e2
  for (let i = n - 1; i > 0; i--) {
    e1 = k * src[today++] + oneMinusK * e1
    e2 = k * e1 + oneMinusK * e2
    tempReal += e2
  }
  let e3 = tempReal / n
  tempReal = e3
  for (let i = n - 1; i > 0; i--) {
    e1 = k * src[today++] + oneMinusK * e1
    e2 = k * e1 + oneMinusK * e2
    e3 = k * e2 + oneMinusK * e3
    tempReal += e3
  }
  let e4 = tempReal / n
  tempReal = e4
  for (let i = n - 1; i > 0; i--) {
    e1 = k * src[today++] + oneMinusK * e1
    e2 = k * e1 + oneMinusK * e2
    e3 = k * e2 + oneMinusK * e3
    e4 = k * e3 + oneMinusK * e4
    tempReal += e4
  }
  let e5 = tempReal / n
  tempReal = e5
  for (let i = n - 1; i > 0; i--) {
    e1 = k * src[today++] + oneMinusK * e1
    e2 = k * e1 + oneMinusK * e2
    e3 = k * e2 + oneMinusK * e3
    e4 = k * e3 + oneMinusK * e4
    e5 = k * e4 + oneMinusK * e5
    tempReal += e5
  }
  let e6 = tempReal / n
  // (TA-Lib's "skip the unstable period" loop never runs: today is already start + 1.)
  tempReal = vfactor * vfactor
  const c1 = -(tempReal * vfactor)
  const c2 = 3 * (tempReal - c1)
  const c3 = -6 * tempReal - 3 * (vfactor - c1)
  const c4 = 1 + 3 * vfactor - c1 + 3 * tempReal
  out[start] = c1 * e6 + c2 * e5 + c3 * e4 + c4 * e3
  while (today < len) {
    e1 = k * src[today++] + oneMinusK * e1
    e2 = k * e1 + oneMinusK * e2
    e3 = k * e2 + oneMinusK * e3
    e4 = k * e3 + oneMinusK * e4
    e5 = k * e4 + oneMinusK * e5
    e6 = k * e5 + oneMinusK * e6
    out[today - 1] = c1 * e6 + c2 * e5 + c3 * e4 + c4 * e3
  }
  return out
}
